export interface QueueItem {
    cmd: string
    cb?: (message: string) => void
    stream?: (line: string) => void
    message?: string
    discard?: boolean
}

export default class Engine {
    public started!: number
    public loaded?: boolean
    public ready?: boolean
    public stream?: (line: string) => void
    public error?: (error: string) => void

    // Debugging flag
    private debugging = import.meta.env.VITE_REACT_APP_ENV !== 'prod'
    private worker!: Worker
    private que: QueueItem[] = []
    private readonly evalRegex = /Total Evaluation[\s\S]+\n$/

    constructor(workerPath?: string) {
        try {
            this.started = Date.now()
            this.worker = this.newWorker(workerPath)
            this.worker.onmessage = this.onMessage.bind(this)
            this.worker.onerror = (error) => this.onError(error.message)
        }
        catch (error) {
            this.onError((error as Error).message)
        }
    }

    // Creates a new Web Worker (browser only)
    private newWorker(workerPath?: string): Worker {
        // Default to a relative URL to your Stockfish engine file in the public folder.
        const url = workerPath || '/stockfish/stockfish.js'
        if (typeof Worker !== 'undefined') {
            return new Worker(new URL(url, import.meta.url))
        }
        throw new Error('Web Workers are not supported in this environment.')
    }

    // --- Message Handling Methods ---
    private getFirstWord(line: string): string {
        const spaceIndex = line.indexOf(' ')
        return spaceIndex === -1 ? line : line.substr(0, spaceIndex)
    }

    private determineQueNum(line: string, que: QueueItem[]): number {
        let cmdType: string | undefined
        const firstWord = this.getFirstWord(line)
        let cmdFirstWord: string
        if (que.length > 0 && que[0].cmd !== 'bench' && que[0].cmd !== 'perft') {
            if (firstWord === 'uciok' || firstWord === 'option') {
                cmdType = 'uci'
            } else if (firstWord === 'readyok') {
                cmdType = 'isready'
            } else if (firstWord === 'bestmove' || firstWord === 'info') {
                cmdType = 'go'
            } else {
                cmdType = 'other'
            }
            for (let i = 0, len = que.length; i < len; i++) {
                cmdFirstWord = this.getFirstWord(que[i].cmd)
                if (cmdFirstWord === cmdType ||
                    (cmdType === 'other' && (cmdFirstWord === 'd' || cmdFirstWord === 'eval'))) {
                    return i
                }
            }
        }
        return 0
    }

    private onMessage(e: string | { data: string }): void {
        const line = typeof e === 'string' ? e : e.data
        let done: boolean | undefined
        let queNum = 0
        let myQue: QueueItem
        let split: string[]
        // If multiple lines, split and process each one
        if (line.indexOf('\n') > -1) {
            split = line.split('\n')
            split.forEach((l) => this.onMessage(l))
            return
        }
        if (this.debugging) {
            console.log('stockfish (onMessage): ' + line)
        }
        if (this.stream) {
            this.stream(line)
        }
        // Ignore lines that do not need processing
        if (this.que.length === 0 ||
            line.substr(0, 14) === 'No such option' ||
            line.substr(0, 3) === 'id ' ||
            line.substr(0, 9) === 'Stockfish') {
            return
        }
        queNum = this.determineQueNum(line, this.que)
        myQue = this.que[queNum]
        if (!myQue) {
            return
        }
        if (myQue.stream) {
            myQue.stream(line)
        }
        if (typeof myQue.message === 'undefined') {
            myQue.message = ''
        } else if (myQue.message !== '') {
            myQue.message += '\n'
        }
        myQue.message += line
        if (line === 'uciok') {
            done = true
            this.loaded = true
        } else if (line === 'readyok') {
            done = true
            this.ready = true
        } else if (line.substr(0, 8) === 'bestmove' && myQue.cmd !== 'bench') {
            done = true
            myQue.message = line
        } else if (myQue.cmd === 'd') {
            if (line.substr(0, 15) === 'Legal uci moves' ||
                line.substr(0, 6) === 'Key is') {
                myQue.discard = true
                done = true
                if (line === 'Key is') {
                    myQue.message = myQue.message.slice(0, -7)
                }
            }
        } else if (myQue.cmd === 'eval') {
            if (this.evalRegex.test(myQue.message)) {
                done = true
            }
        } else if (line.substr(0, 8) === 'pawn key' ||
            line.substr(0, 12) === 'Nodes/second' ||
            line.substr(0, 15) === 'Unknown command') {
            done = true
        }
        if (done) {
            this.que.splice(queNum, 1)
            if (myQue.cb && !myQue.discard) {
                myQue.cb(myQue.message || '')
            }
        }
    }

    private onError(error: string): void {
        if (this.debugging) {
            console.log('stockfish (onError):', error)
        }
        if (this.error) {
            this.error(error)
        }
    }

    // --- Public API Methods ---
    public send(cmd: string, cb?: (message: string) => void, stream?: (line: string) => void): void {
        let noReply: boolean | undefined
        cmd = cmd.trim()
        if (this.debugging) {
            console.log('stockfish (send): ' + cmd)
        }
        if (
            cmd !== 'ucinewgame' &&
            cmd !== 'flip' &&
            cmd !== 'stop' &&
            cmd !== 'ponderhit' &&
            cmd.substr(0, 8) !== 'position' &&
            cmd.substr(0, 9) !== 'setoption'
        ) {
            this.que.push({ cmd, cb, stream })
        } else {
            noReply = true
        }
        this.worker.postMessage(cmd)
        if (noReply && cb) {
            setTimeout(cb, 0)
        }
    }

    public stopMoves(): void {
        const len = this.que.length
        for (let i = 0; i < len; i++) {
            if (this.debugging) {
                console.log('stockfish (stopMoves):', i, this.getFirstWord(this.que[i].cmd))
            }
            if (this.getFirstWord(this.que[i].cmd) === 'go' && !this.que[i].discard) {
                this.send('stop')
                this.que[i].discard = true
            }
        }
    }

    public getCueLen(): number {
        return this.que.length
    }

    public quit(): void {
        if (this.worker && this.worker.terminate) {
            this.worker.terminate();
            // Dereference the worker
            (this.worker as any) = null
            this.ready = undefined
        }
    }
}